"use client";

import * as React from "react";
import { useState } from "react";
import { formatShortDate } from "@/lib/date/format-short-date";

import { Calendar } from "@/components/ui/calendar";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { Icon } from "@/components/ui/icon";
import { Select } from "@/components/ui/select";
import useIsMobile from "@/hooks/use-is-mobile";

type DateInputProps = {
  value?: Date | null;
  onChange: (value: Date | undefined | null) => void;
  placeholder?: string;
} & Omit<
  React.ComponentProps<typeof Select.Trigger>,
  "value" | "onChange" | "children"
>;

export function DateInput({
  value,
  onChange,
  placeholder = "Pick a date",
  className,
  variant,
  ...triggerProps
}: DateInputProps) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const handleSelect = (next: Date | undefined | null) => {
    onChange(next);
    setOpen(false);
  };

  if (isMobile) {
    return (
      <>
        {/* `open` is always false here — the Select popover/drawer must
            never open on mobile. The trigger's onClick is intercepted below
            to drive our own bottom Drawer instead, keeping the trigger's
            markup/styling identical to desktop. */}
        <Select.Root open={false} responsive={false}>
          <Select.Trigger
            variant={variant}
            className={className}
            active={!!value}
            {...triggerProps}
            onClick={(e) => {
              triggerProps.onClick?.(e);
              setOpen(true);
            }}
          >
            <Icon name="IconCalendar1" size={14} className="shrink-0" />
            <Select.TriggerContent>
              {value ? formatShortDate(value) : placeholder}
            </Select.TriggerContent>
            <Select.Icon className="ml-auto" />
          </Select.Trigger>
        </Select.Root>

        <Drawer open={open} onOpenChange={setOpen} direction="bottom">
          <DrawerContent className="items-center">
            <DrawerTitle className="sr-only">{placeholder}</DrawerTitle>
            <Calendar
              mode="single"
              selected={value || undefined}
              onSelect={handleSelect}
            />
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <Select.Root open={open} onOpenChange={setOpen}>
      <Select.Trigger
        variant={variant}
        className={className}
        active={!!value}
        {...triggerProps}
      >
        <Icon name="IconCalendar1" size={14} className="shrink-0" />
        <Select.TriggerContent>
          {value ? formatShortDate(value) : placeholder}
        </Select.TriggerContent>
        <Select.Icon className="ml-auto" />
      </Select.Trigger>

      <Select.Content matchTriggerWidth={false} align="start">
        <Calendar
          mode="single"
          selected={value || undefined}
          onSelect={handleSelect}
        />
      </Select.Content>
    </Select.Root>
  );
}
